import { useState, type ComponentProps } from 'react'
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table'

import { Button } from '@atoms'
import { DataTableContent, DataTableRoot } from '@components/molecules/DataTable'

import type { ComponentDoc, PropDocs } from '../types'

type HighlightProps = Pick<ComponentProps<typeof DataTableContent>, 'highlightedRowId'>

const props: PropDocs<HighlightProps> = {
  highlightedRowId: {
    type: 'string | null',
    description: 'The id of the row to highlight, as getRowId returns it. It scrolls into view and is tinted.',
  },
}

type CallList = { href: string; name: string; supporters: number }

const CALL_LISTS: CallList[] = [
  { href: '/call_lists/1', name: 'Lapsed donors', supporters: 214 },
  { href: '/call_lists/2', name: 'Doorknock follow-up', supporters: 58 },
  { href: '/call_lists/3', name: 'New volunteers', supporters: 31 },
]

const columns: ColumnDef<CallList>[] = [
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'supporters', header: 'Supporters' },
]

function Demo() {
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null)
  const table = useReactTable({
    data: CALL_LISTS,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (callList) => callList.href,
  })

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:items-start tw:gap-3">
      <Button rank="secondary" size="sm" onClick={() => setHighlightedRowId('/call_lists/2')}>
        Highlight Doorknock follow-up
      </Button>
      <div className="tw:w-full">
        <DataTableRoot table={table}>
          <DataTableContent highlightedRowId={highlightedRowId} />
        </DataTableRoot>
      </div>
    </div>
  )
}

const doc: ComponentDoc<HighlightProps> = {
  slug: 'highlighted-row',
  name: 'Highlighted row',
  tier: 'pattern',
  description:
    'Points out one row in a DataTable. The row scrolls into view and stays tinted for the visit. Use it when a link or a just-finished action is about that row.',
  importStatement: "import { DataTableRoot, DataTableContent } from '@components/molecules/DataTable'",
  sourcePath: 'app/frontend/components/molecules/DataTable/DataTableContent.tsx',
  props,
  examples: [
    {
      title: 'After an action',
      note: 'Pass the id of the row the action made or changed, as getRowId returns it. The row gets a data-highlighted attribute for tests.',
      render: () => <Demo />,
      code: [
        'const table = useReactTable({',
        '  data: callLists,',
        '  columns,',
        '  getCoreRowModel: getCoreRowModel(),',
        '  getRowId: (callList) => callList.href,',
        '})',
        '',
        '<DataTableRoot table={table}>',
        '  <DataTableContent highlightedRowId={createdHref} />',
        '</DataTableRoot>',
      ].join('\n'),
    },
  ],
  usage: {
    do: [
      'A link opens the page to show one row, such as a callback from a notification.',
      'An action just made or changed a row, such as a new call list.',
    ],
    dont: [
      'More than one row. Highlight the one the user asked about.',
      'Selection. Use a checkbox column.',
      'A row on another page of a paginated table. It is not rendered, so it cannot scroll into view.',
    ],
  },
  a11y: [
    'The tint is not announced. Move focus to a link or button in the row so keyboard and screen reader users land on it.',
    "Moving focus is the page's job. The table only scrolls and tints.",
  ],
}

export default doc
